import FJMediaCacheStore from "./FJMediaCacheStore";
import FJUtil from "../util/FJUtil";
import FJCanvasManager from "../core/FJCanvasManager";
import {FJDecoderManager} from "fj-codec";
import FJCoreUtil from "../core/util/FJCoreUtil";

const MEDIA_TYPE = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  AUDIO: 'AUDIO',
};

const MEDIA_FROM_TYPE = {
  STOCK: 'STOCK', // stock资源
  UNSPLASH: 'UNSPLASH', // unsplash资源
  SELF: 'SELF', // 自身服务器资源
  LOCAL: 'LOCAL',  // 本地资源
  TEMPLATE: 'TEMPLATE', // 模板资源
};

const MEDIA_STATUS = {
  SUCCESS: 'SUCCESS',
  LINK: 'LINK',
  DOWNLOAD: 'DOWNLOAD',
  DOWNLOADING: 'DOWNLOADING',
  PENDING: 'PENDING', // 正在缓存
  LOADING: 'LOADING',
  ERROR: 'ERROR',
	RETRY: 'RETRY',
};

const CLOUD_STATUS = {
  DONE: 'DONE',             // 已上传
  UPLOADING: 'UPLOADING',   // 正在上传
  WAITING:'WAITING',        // 等待上传
  UNDONE: "UNDONE",         // 未上传
  FAILED: "FAILED",         // 上传失败
};


/**
 * 媒体对象
 * @class
 * @classdesc 通用媒体对象，包括图片、视频、音频等
 */
class FJMedia {

  constructor(props) {
    this._id = props && props.id;
    this._status = (props && props.status) || MEDIA_STATUS.SUCCESS;
    this._cloudStatus = (props && props.cloudStatus) || CLOUD_STATUS.UNDONE;
    this._fromType = props && props.fromType;
    this._data = props && props.data;
    this._type = props && props.type;
    this._downloadProgress = props && props.downloadProgress;
    this._uploadProgress = 0;
    this._name = props && props.name;
    this._duration = props && props.duration;
    this._size = props && props.size;
    this._thumbnail = props && props.thumbnail;
    this._rotation = 0;
  }

  /**
   * @description 返回媒体的所有类型
   * @return {{IMAGE: string, VIDEO: string, AUDIO: string}}
   * @constructor
   */
  static MediaType() {
    return MEDIA_TYPE;
  }

  /**
   * @description 返回媒体的所有来源
   * @return {{LOCAL: string, STOCK: string, SELF: string, TEMPLATE: string, UNSPLASH: string}}
   * @constructor
   */
  static MediaFromType() {
    return MEDIA_FROM_TYPE;
  }

  /**
   * @description 返回媒体的所有状态
   * @return {{SUCCESS: string, DOWNLOAD: string, LINK: string, DOWNLOADING: string, RETRY: tring}}
   * @constructor
   */
  static MediaStatus() {
    return MEDIA_STATUS;
  }

  /**
   * @description 返回媒体的云空间状态
   * @returns {{DONE: string, UPLOADING: string, FAILED: string, WAITING: string, UNDONE: string}}
   * @constructor
   */
  static CLOUD_STATUS() {
    return CLOUD_STATUS;
  }

  /**
   * @description 返回资源id
   * @return {string | number}
   */
  get id() {
    return this._id;
  }

  /**
   * @description 返回资源来源
   * @return {MEDIA_FROM_TYPE}
   */
  get fromType() {
    return this._fromType;
  }

  /**
   * @description 返回该资源类型
   * @return {*}
   */
  get type() {
    return this._type;
  }

  /**
   * @description 返回该资源名称
   * @return {*}
   */
  get name() {
    return this._name;
  }

  /**
   * @description 返回该资源时长
   * @return {*}
   */
  get duration() {
    return this._duration;
  }

  /**
   * @description 返回该资源的尺寸
   * @return {*}
   */
  get size() {
    return this._size;
  }

  /**
   * @description 返回缩略图
   * @return {*|string}
   */
  get thumbnail() {
    return this._thumbnail;
  }

  /**
   * @description 返回资源旋转信息
   * @return {number}
   */
  get rotation() {
    return this._rotation;
  }

  get cloudStatus() {
    return this._cloudStatus;
  }

  encode() {
    return {
      id: this._id,
      type: this._type,
      fromType: this._fromType,
    }
  }

  async init() {
  	if (!this._data.getUrl()) {
  		await this._data.initData();
	  }
    let url = this._data.getUrl();
    let blob = this._data.getBlob();
    let canvas = FJCanvasManager.getCanvas();
    canvas.width = 160;
    canvas.height = 90;
    switch (this._type) {
      case MEDIA_TYPE.VIDEO: {
        let info = null;
        if (FJCoreUtil.getBrowser() === 'FF') {
          info = await new Promise((resolve) => {
            FJDecoderManager.getVideoInfo(blob, resolve);
          });
        }
        if (!info || info.duration < 0) {
          let data = await FJUtil.getVideoInfoWithUrl(url);
          if (info) {
            info.duration = data.duration;
          } else {
            info = data;
          }
        } else {
          this._rotation = info.rotate || 0;
        }
        this._duration = info.duration;
        this._size = {
          width: info.width,
          height: info.height
        };
        try {
          await FJUtil.renderVideoCoverThumbnail(canvas, url, {...this._size, rotation: this._rotation});
          this._thumbnail = canvas.toDataURL();
        } catch (e) {
          console.log(e);
          throw new Error('renderVideoCoverThumbnail fail');
        }
        break;
      }
      case MEDIA_TYPE.IMAGE: {
        await FJUtil.renderImageThumbnail(canvas, url);
        this._thumbnail = canvas.toDataURL();
        break;
      }
      case MEDIA_TYPE.AUDIO: {
        this._duration = await FJUtil.getAudioDurationWithUrl(url);
        break;
      }
      default:
    }

    FJCanvasManager.releaseCanvas(canvas);
  }

  /**
   * @description 返回媒体状态
   * @return {string}
   */
  getStatus() {
    return this._status;
  }

  /**
   * @description 设置媒体状态
   * @param {MEDIA_STATUS} status
   */
  setStatus(status) {
    this._status = status;
  }

  /**
   * @description 设置媒体云端状态
   * @param {CLOUD_STATUS} status
   */
  setCloudStatus(status) {
    this._cloudStatus = status;
  }

  /**
   * @description 返回资源
   * @return {MediaCache}
   */
  getMediaCache() {
    return this._data;
  }

  /**
   * @description 更新资源
   * @param mediaCache
   */
  setMediaCache(mediaCache) {
    this._data = mediaCache;
  }

  /**
   * @description 根据文件更新资源
   * @param file
   * @return {Promise<void>}
   */
  async setMediaCacheWithFile(file) {
    this._data = await FJMediaCacheStore.setMediaCache(this._id, file);
    let { RETAIN } = FJMediaCacheStore.eventType;
    FJMediaCacheStore.dispatch(RETAIN, this._id);
  }

  /**
   * @description 返回媒体下载进度
   * @return {number}
   */
  getProgress() {
    return this._downloadProgress;
  }

  /**
   * @description 更新媒体下载进度
   * @param {number} progress
   */
  setProgress(progress) {
    this._downloadProgress = progress;
  }

  /**
   * @description 返回媒体上传进度
   * @return {number}
   */
  getUploadProgress() {
    return this._uploadProgress;
  }

  /**
   * @description 更新媒体上传进度
   * @param {number} progress
   */
  setUploadProgress(progress) {
    this._uploadProgress = progress;
  }
}

export default FJMedia;
